import {
	type Cell,
	flexRender,
	getCoreRowModel,
	type Table,
	type Row as TanstackRow,
	useReactTable,
} from "@tanstack/react-table";
import type React from "react";
import { memo, useEffect, useMemo, useRef } from "react";
import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import { DataTableResizeHandle } from "@/shared/components/ui/data-table-resize-handle";
import {
	useColumnSizeVars,
	useGridTemplateColumns,
} from "@/shared/components/ui/data-table-utils";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { cn } from "@/shared/lib/utils";
import {
	entriesColumns,
	entryHasStatusBadge,
	isEntryIncomplete,
} from "./columns";
import styles from "./EntriesTable.module.css";
import type {
	EntriesTableMeta,
	EntriesTableMode,
	Entry,
	Matter,
	SortDirection,
	SortingMode,
} from "./types";
import { useEntriesTableState } from "./useEntriesTableState";
import { useRegisterColumnPicker } from "../HeaderBar/ColumnPickerContext";

export { formatTime } from "./formatTime";
export type {
	EntriesTableMode,
	Entry,
	EntryStatus,
	Matter,
	SortDirection,
	SortingMode,
} from "./types";
export type { UseEntryTableSelectionResult } from "./useEntryTableSelection";
export { useEntryTableSelection } from "./useEntryTableSelection";

export interface EntriesTableProps {
	/** Source data — flat array. */
	entries: Entry[];
	/**
	 * Pre-grouped data; the table renders a group header before each group's rows.
	 * Keyed by date or hour label or matter name (consumer-driven).
	 */
	groupedEntries: Record<string, Entry[]>;

	/** Multi-select state (use `useEntryTableSelection` to produce these). Pass empty Set + noops to disable selection. */
	checkedRows: Set<number>;
	selectAllChecked: boolean;
	selectAllIndeterminate: boolean;
	onToggleSelectAll: () => void;
	onToggleRow: (id: number, e: React.MouseEvent) => void;

	/** Row click — typically opens an edit/detail modal. Suppressed when the row is exporting. */
	onRowClick?: (entry: Entry) => void;

	/** Header click handlers. The resize handle stops propagation so dragging never sorts. */
	onSortByMatter?: () => void;
	onSortByTime?: () => void;

	showCheckboxes?: boolean;
	mode?: EntriesTableMode;
	matters?: Matter[];

	/** Right-padding inside cells, in px. Use for admin to prevent content touching dividers. */
	cellPadding?: number;

	sortingMode?: SortingMode;
	sortDirection?: SortDirection;

	failedExportEntries?: Map<number, string>;
	exportingEntryIds?: Set<number>;
	mutatingIds?: Set<number>;

	emptyMessage?: string;
	getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[];
	autoScrollToBottom?: boolean;

	/**
	 * Per-table localStorage key for column sizing + visibility.
	 * Different views should pass different keys to keep their preferences independent.
	 */
	columnStorageKey: string;
}

// ============================================================================
// Row — memoizable, gets its visible cells from TanStack and renders them
// into the row grid. Visual state (failed/exporting/mutating) comes in as
// already-resolved booleans so unchanged rows skip re-render even when the
// parent's Set/Map references update.
// ============================================================================
type RowProps = {
	row: TanstackRow<Entry>;
	/**
	 * Pre-computed via `row.getVisibleCells()` and passed in so React.memo can
	 * detect visibility changes — TanStack memoizes the array reference, so
	 * unchanged visibility produces a stable reference and skips re-render.
	 */
	cells: Cell<Entry, unknown>[];
	/**
	 * Per-row selection state. Passed in as a primitive so React.memo invalidates
	 * for this single row when the user toggles its checkbox — without this prop
	 * the checkbox cell (which reads meta.checkedRows) wouldn't re-render.
	 */
	isChecked: boolean;
	isExporting: boolean;
	isMutating: boolean;
	isFailed: boolean;
	isIncomplete: boolean;
	/** Soft "needs attention" state: unresolved attorney feedback. */
	hasWarning: boolean;
	showCheckboxes: boolean;
	onRowClick?: (entry: Entry) => void;
	onToggleRow: (id: number, e: React.MouseEvent) => void;
};

// Intentionally NOT React.memo'd. Earlier I memoized Row hoping to skip cheap
// re-renders, but it caused the visible checkbox state to lag behind toggles
// for reasons I couldn't pin down — most likely the prop diff via TanStack's
// memoized cell array wasn't kicking React.memo's invalidation in the way I
// expected. Drag perf is handled by MemoizedBody wrapping the whole body, so
// row-level memo wasn't load-bearing anyway.
function Row({
	row,
	cells,
	isChecked,
	isExporting,
	isMutating,
	isFailed,
	isIncomplete,
	hasWarning,
	showCheckboxes,
	onRowClick,
	onToggleRow,
}: RowProps) {
	const entry = row.original;
	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: row is a clickable record; keyboard navigation handled via the embedded checkbox + the edit modal's focus management
		// biome-ignore lint/a11y/useKeyWithClickEvents: same as above
		<div
			className={cn(
				styles.row,
				isFailed
					? styles.failedRow
					: isIncomplete
						? styles.incompleteRow
						: hasWarning
							? styles.warningRow
							: undefined,
				isExporting && styles.exportingRow,
				isMutating && styles.mutatingRow,
			)}
			onClick={(e) => {
				if (isExporting) return;
				const target = e.target as HTMLElement;
				if (target.closest(".item-checkbox") || target.closest("label")) return;
				if (showCheckboxes && (e.shiftKey || e.ctrlKey || e.metaKey)) {
					e.preventDefault();
					window.getSelection()?.removeAllRanges();
					onToggleRow(entry.id, e);
					return;
				}
				onRowClick?.(entry);
			}}
		>
			{cells.map((cell) => {
				const meta = cell.column.columnDef.meta;
				const isUnits = cell.column.id === "units";
				const isDate = cell.column.id === "date";
				const isSelect = cell.column.id === "select";
				return (
					<div
						key={cell.id}
						className={cn(
							styles.cell,
							isUnits && styles.cellUnits,
							isDate && styles.cellNowrap,
							meta?.cellClassName,
						)}
					>
						{isSelect
							? // Render the checkbox directly from Row props rather than
								// routing through the TanStack cell function. The cell function
								// would have to read meta.checkedRows at every render, and
								// although the chain *should* be reactive, in practice the
								// flexRender → cell function → context.table.options.meta
								// indirection produced a stale visual state for single-row
								// toggles (header count updated but the checkbox didn't tick).
								// Direct render makes the checked attribute a function of
								// isChecked which is a Row prop → memo invalidates correctly.
								showCheckboxes && (
									// biome-ignore lint/a11y/useKeyWithClickEvents: same as row click — checkbox inside is the keyboard target
									<label
										className={styles.checkboxHitArea}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											onToggleRow(entry.id, e);
										}}
									>
										<input
											className="item-checkbox"
											type="checkbox"
											checked={isChecked}
											readOnly
										/>
									</label>
								)
							: flexRender(cell.column.columnDef.cell, cell.getContext())}
					</div>
				);
			})}
		</div>
	);
}

// ============================================================================
// Body — iterates groupedEntries, rendering a group header before each group's
// rows. Comes in two flavors: a plain Body that re-renders normally, and a
// React.memo'd MemoizedBody used during column-resize drags (the CSS-var trick
// from TanStack docs — cells read widths from `var(--col-X-size)` on the root,
// so we can skip the body during drag and let one inline-style update on root
// drive the visual resize).
// ============================================================================
type BodyProps = {
	table: Table<Entry>;
	groupedEntries: Record<string, Entry[]>;
	sortingMode: SortingMode;
	checkedRows: Set<number>;
	failedExportEntries: Map<number, string>;
	exportingEntryIds: Set<number>;
	mutatingIds: Set<number>;
	showCheckboxes: boolean;
	onRowClick?: (entry: Entry) => void;
	onToggleRow: (id: number, e: React.MouseEvent) => void;
};

function Body({
	table,
	groupedEntries,
	sortingMode,
	checkedRows,
	failedExportEntries,
	exportingEntryIds,
	mutatingIds,
	showCheckboxes,
	onRowClick,
	onToggleRow,
}: BodyProps) {
	// `matters` (needed for incomplete check) lives on table.options.meta — pull
	// it once per Body render rather than per row.
	const meta = table.options.meta as EntriesTableMeta;
	const matters = meta.matters;
	const mode = meta.mode;
	// Don't show the soft warning ribbon in history/deleted views — those entries
	// are settled and "incomplete" no longer applies semantically.
	const warningEnabled = mode !== "history" && mode !== "deleted";
	return (
		<>
			{Object.entries(groupedEntries).map(([groupKey, groupRows]) => {
				const groupTotal = groupRows.reduce(
					(sum, entry) => sum + (entry.duration || 0),
					0,
				);
				return (
					<div key={groupKey}>
						<div className={styles.dateGroupHeader}>
							<div className={styles.dateText}>{groupKey}</div>
							{sortingMode === "matter" && (
								<div className={styles.groupTotal}>
									{groupTotal.toFixed(1)} hrs
								</div>
							)}
						</div>
						{groupRows.map((entry) => {
							// Use rowsById (returns undefined) rather than getRow (throws).
							// During optimistic delete, `entries` may already lack a row while
							// `groupedEntries` (computed in a useEffect) still references it
							// for one render.
							const tanstackRow =
								table.getRowModel().rowsById[String(entry.id)];
							if (!tanstackRow) return null;
							// Defensive id coercion: TS types say `entry.id: number`, but the
							// API can return string IDs in some paths and the failed/exporting
							// Maps are populated via `Number(f.id)`. Mixing string and number
							// keys silently breaks `.has()`. Test both representations.
							const numId = Number(entry.id);
							const isFailed =
								failedExportEntries.has(numId) ||
								failedExportEntries.has(entry.id);
							const isExporting =
								exportingEntryIds.has(numId) ||
								exportingEntryIds.has(entry.id);
							const isMutating =
								mutatingIds.has(numId) || mutatingIds.has(entry.id);
							const isChecked =
								checkedRows.has(numId) || checkedRows.has(entry.id);
							const isIncomplete =
								warningEnabled &&
								!isFailed &&
								isEntryIncomplete(entry, matters);
							const hasWarning =
								warningEnabled &&
								!isFailed &&
								!isIncomplete &&
								Boolean(entry.attorney_feedback);
							return (
								<Row
									key={entry.id}
									row={tanstackRow}
									cells={tanstackRow.getVisibleCells()}
									isChecked={isChecked}
									isFailed={isFailed}
									isIncomplete={isIncomplete}
									hasWarning={hasWarning}
									isExporting={isExporting}
									isMutating={isMutating}
									showCheckboxes={showCheckboxes}
									onRowClick={onRowClick}
									onToggleRow={onToggleRow}
								/>
							);
						})}
					</div>
				);
			})}
		</>
	);
}

const MemoizedBody = memo(Body) as typeof Body;

// ============================================================================
// EntriesTable — wires data + columns + persisted column state, renders the
// header (with resize handles + sort triggers), the scroll container, and the
// body (memoized during resize for 60fps drag).
// ============================================================================
const EntriesTable: React.FC<EntriesTableProps> = ({
	entries,
	groupedEntries,
	checkedRows,
	selectAllChecked,
	selectAllIndeterminate,
	onToggleSelectAll,
	onToggleRow,
	onRowClick,
	onSortByMatter,
	onSortByTime,
	showCheckboxes = true,
	mode = "entries",
	matters = [],
	cellPadding = 12,
	sortingMode = "time",
	sortDirection = "desc",
	failedExportEntries = new Map() as Map<number, string>,
	exportingEntryIds = new Set() as Set<number>,
	mutatingIds = new Set() as Set<number>,
	emptyMessage = "No transactions to see here...",
	getRestrictedWords,
	autoScrollToBottom = false,
	columnStorageKey,
}) => {
	const { darkMode } = useDarkMode();
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const hasInitiallyScrolled = useRef(false);

	const {
		columnSizing,
		columnVisibility,
		onColumnSizingChange,
		onColumnVisibilityChange,
	} = useEntriesTableState(columnStorageKey);

	const hideableColumns = useMemo(
		() =>
			entriesColumns
				.filter((c) => c.enableHiding !== false)
				.map((c) => ({
					id: c.id as string,
					label: (c.meta?.label ?? c.id) as string,
				})),
		[],
	);
	const pickerHandle = useMemo(
		() => ({
			columns: hideableColumns,
			visibility: columnVisibility,
			onToggle: (id: string) =>
				onColumnVisibilityChange((prev) => ({
					...prev,
					[id]: prev[id] === false,
				})),
		}),
		[hideableColumns, columnVisibility, onColumnVisibilityChange],
	);
	useRegisterColumnPicker(pickerHandle);

	// Data-driven: only show the status column when at least one visible entry
	// would render a badge in it. This keeps the table free of empty whitespace
	// in the common "clean tracked entries" case while still surfacing the
	// column the moment something needs attention.
	const showStatusColumn = useMemo(
		() =>
			entries.some((e) =>
				entryHasStatusBadge(
					e,
					mode,
					matters,
					exportingEntryIds,
					failedExportEntries,
				),
			),
		[entries, mode, matters, exportingEntryIds, failedExportEntries],
	);

	// Force-hide the select column when checkboxes are disabled, regardless of
	// user-toggled visibility. (`select` has enableHiding=false so it never
	// appears in the picker; this is a separate dimension.) Same pattern for
	// `status` — its visibility is data-driven, not user-controlled.
	const effectiveVisibility = useMemo(
		() => ({
			...columnVisibility,
			select: showCheckboxes,
			status: showStatusColumn,
		}),
		[columnVisibility, showCheckboxes, showStatusColumn],
	);

	const meta: EntriesTableMeta = useMemo(
		() => ({
			darkMode,
			showCheckboxes,
			checkedRows,
			selectAllChecked,
			selectAllIndeterminate,
			onToggleSelectAll,
			onToggleRow,
			matters,
			sortingMode,
			sortDirection,
			onSortByTime,
			onSortByMatter,
			failedExportEntries,
			exportingEntryIds,
			mutatingIds,
			mode,
			cellPadding,
			getRestrictedWords,
		}),
		[
			darkMode,
			showCheckboxes,
			checkedRows,
			selectAllChecked,
			selectAllIndeterminate,
			onToggleSelectAll,
			onToggleRow,
			matters,
			sortingMode,
			sortDirection,
			onSortByTime,
			onSortByMatter,
			failedExportEntries,
			exportingEntryIds,
			mutatingIds,
			mode,
			cellPadding,
			getRestrictedWords,
		],
	);

	const table = useReactTable<Entry>({
		data: entries,
		columns: entriesColumns,
		state: {
			columnSizing,
			columnVisibility: effectiveVisibility,
		},
		onColumnSizingChange,
		onColumnVisibilityChange,
		columnResizeMode: "onChange",
		columnResizeDirection: "ltr",
		enableColumnResizing: true,
		getRowId: (row) => String(row.id),
		getCoreRowModel: getCoreRowModel(),
		meta,
	});

	const columnSizeVars = useColumnSizeVars(table);
	const gridTemplate = useGridTemplateColumns(table);
	const isResizing =
		table.getState().columnSizingInfo.isResizingColumn !== false &&
		table.getState().columnSizingInfo.isResizingColumn !== null &&
		table.getState().columnSizingInfo.isResizingColumn !== "";

	// Auto-scroll: bottom for today (autoScrollToBottom=true), top for everything
	// else on initial load; preserve position on subsequent refreshes.
	// Date changes are handled by `key` prop on <EntriesTable> from consumers.
	// biome-ignore lint/correctness/useExhaustiveDependencies: groupedEntries reference triggers refresh
	useEffect(() => {
		if (!scrollContainerRef.current || entries.length === 0) return;
		if (autoScrollToBottom) {
			scrollContainerRef.current.scrollTop =
				scrollContainerRef.current.scrollHeight;
		} else if (!hasInitiallyScrolled.current) {
			scrollContainerRef.current.scrollTop = 0;
			hasInitiallyScrolled.current = true;
		}
	}, [autoScrollToBottom, groupedEntries, entries.length]);

	const rootStyle = useMemo(
		() =>
			({
				...columnSizeVars,
				"--entries-grid-template": gridTemplate,
				"--entries-cell-padding": `${cellPadding}px`,
			}) as React.CSSProperties,
		[columnSizeVars, gridTemplate, cellPadding],
	);

	return (
		<div className={styles.tableRoot} style={rootStyle}>
			<div className={styles.headerRow}>
				{table.getHeaderGroups().map((headerGroup) =>
					headerGroup.headers.map((header) => {
						const id = header.column.id;
						const sortable =
							(id === "date" && onSortByTime) ||
							(id === "matter" && onSortByMatter);
						const onClick =
							id === "date"
								? onSortByTime
								: id === "matter"
									? onSortByMatter
									: undefined;
						const headerMeta = header.column.columnDef.meta;
						return (
							// biome-ignore lint/a11y/noStaticElementInteractions: header click sorts; visual cursor change and tooltip indicate intent
							// biome-ignore lint/a11y/useKeyWithClickEvents: same
							<div
								key={header.id}
								className={cn(
									styles.headerCell,
									sortable && styles.headerCellSortable,
									id === "units" && styles.headerCellUnits,
									headerMeta?.headerClassName,
								)}
								onClick={onClick}
							>
								{flexRender(
									header.column.columnDef.header,
									header.getContext(),
								)}
								<DataTableResizeHandle header={header} />
							</div>
						);
					}),
				)}
			</div>
			<div className={styles.scrollContainer} ref={scrollContainerRef}>
				{entries.length === 0 ? (
					<div className={styles.emptyState}>
						<div className={styles.emptyText}>{emptyMessage}</div>
					</div>
				) : isResizing ? (
					<MemoizedBody
						table={table}
						groupedEntries={groupedEntries}
						sortingMode={sortingMode}
						checkedRows={checkedRows}
						failedExportEntries={failedExportEntries}
						exportingEntryIds={exportingEntryIds}
						mutatingIds={mutatingIds}
						showCheckboxes={showCheckboxes}
						onRowClick={onRowClick}
						onToggleRow={onToggleRow}
					/>
				) : (
					<Body
						table={table}
						groupedEntries={groupedEntries}
						sortingMode={sortingMode}
						checkedRows={checkedRows}
						failedExportEntries={failedExportEntries}
						exportingEntryIds={exportingEntryIds}
						mutatingIds={mutatingIds}
						showCheckboxes={showCheckboxes}
						onRowClick={onRowClick}
						onToggleRow={onToggleRow}
					/>
				)}
			</div>
		</div>
	);
};

export default EntriesTable;
